import React, { Dispatch, KeyboardEventHandler, SetStateAction, useCallback, useEffect, useState } from 'react';
import Icon from '@ant-design/icons';
import { Button, Select, Typography } from 'antd';
import Form from 'antd/es/form';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { debounce } from 'chart.js/helpers';
import { useContextUsers } from '../../../../../../../context/users';
import { IReportProperty, useReportLocationSearch, useReportPropertyDetails } from '../../../../../../../hooks/reports';
import { Location, Plus, Close } from '../../../../../../Common/Icon';
import Loading from '../../../../../../Common/Loading';
import { DEFAULT_COLUMN } from '../../../../constants';
import styles from '../../index.module.less';
import { useContextReports } from '../../../../../../../context/reports';
import { commafy, toFloat, toFixed } from '../../../../../../../utils/text';
import { EnumReportTypes } from '../../../../../../../types/reports';
import { calculateUip } from '../../../../../../../utils/loan';
import { formatAmount } from '../../../../../../../utils/formatAmount';
import { calculateLoanToValue } from '../../../../../../../utils/calculateLoanToValue';
import { calculateTotal } from '../../../../../../../utils/calculateTotal';
import { calculatePrincipalInterest } from '../../../../../../../utils/calculatePrincipalInterest';
import { calculateVaFundingFee } from '../../../../../../../utils/calculateVaFundingFee';
import { calculateDownPayment } from '../../../../../../../utils/calculateDownPayment';
import { calculateLoanAmount } from '../../../../../../../utils/calculateLoanAmount';
import { calculateHomeInsurance } from '../../../../../../../utils/calculateHomeInsurance';
import { calculateDepositMoneyPrice } from '../../../../../../../utils/calculateDepositMoneyPrice';

interface IControlsCol {
  typeNewColState: [value: 'area' | 'location' | null, setValue: Dispatch<SetStateAction<'area' | 'location' | null>>];
  handleClose: () => void;
}

interface IAddNewCol {
  address?: string | undefined;
  zipCode?: string;
  zpid?: string | undefined;
}

const ControlsCol = ({ typeNewColState, handleClose }: IControlsCol) => {
  const defaultColumn = { ...DEFAULT_COLUMN };
  const form = useFormInstance();
  const { profile } = useContextUsers();
  const { handleLoansUpdate, report, getReportLoanClosingCosts, getReportClosingCosts } = useContextReports();
  const [typeNewCol, setTypeNewCol] = typeNewColState;
  const [searchValue, setSearchValue] = useState<string>('');
  const [properties, setProperties] = useState<IReportProperty | IReportProperty[] | null>();
  const reportLocationSearch = useReportLocationSearch();
  const reportPropertyDetails = useReportPropertyDetails();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (reportLocationSearch.error || reportPropertyDetails.error) {
      setSearchValue('');
    }
  }, [reportLocationSearch.error, reportPropertyDetails.error]);

  const handleAddNewColumn = async ({ address, zipCode, zpid }: IAddNewCol) => {
    if (isLoading) return;

    setSearchValue('')

    const isEmptyAddress = address && address.split(',').some((item) => item.trim() === 'undefined');
    const loans = form.getFieldValue('loans');

    const profilePermissions = profile?.permissions ? JSON.parse(profile.permissions) : {};
    const reportUserPermissions = report?.User?.permissions ? JSON.parse(report.User.permissions) : {};

    const hasPropertyDataCopilot =
      profilePermissions.property_data_copilot || reportUserPermissions.property_data_copilot;
    const isProfileAdmin = profile?.type === 'admin';
    const isRefinance = report?.reportType === EnumReportTypes.refinance;

    if (zpid && (hasPropertyDataCopilot || isProfileAdmin)) {
      await reportPropertyDetails.fetch({ zpid }).then((res) => {
        if (!res?.data) return;

        const {
          purchasePrice,
          propertyTaxes,
          hoaDues,
          supplyMentalTaxBill,
          rentValue,
          suplimentalData,
          priceStatus,
          taxRate,
          state,
          zestimate
        } = res.data;

        const purchasePriceValue = parseFloat(String(purchasePrice)) || 0;

        const propertyValue = purchasePriceValue > zestimate ? purchasePriceValue : zestimate;

        const downPayment = calculateDownPayment(
          form,
          loans?.length,
          {
            purchasePrice: purchasePriceValue,
            propertyValue,
            downPaymentPercentage: 20
          }
        )

        const homeInsurance = calculateHomeInsurance(
          form,
          loans?.length,
          {
            purchasePrice: purchasePriceValue,
            propertyValue,
            homeInsurancePercentage: 0.25
          }
        )

        const loanAmount = calculateLoanAmount(
          form,
          loans?.length,
          {
            purchasePrice: purchasePriceValue,
            propertyValue,
            downPayment
          }
        );

        const interestRate = '6.5'

        const vaFundingFee =
          calculateVaFundingFee(
            loanAmount,
            defaultColumn?.externalLoan?.vaFundingFeePercentage || 2.30
          )

        const principalInterest = calculatePrincipalInterest({
          loanAmount: { value: loanAmount },
          interestRate: { value: interestRate },
          loanPeriodInYears: { value: defaultColumn.loanPeriodInYears.value },
        })

        const principalInterestWithRateShift = calculatePrincipalInterest(
          {
            loanAmount: { value: loanAmount },
            loanPeriodInYears: { value: defaultColumn.loanPeriodInYears.value },
          },
          parseFloat(interestRate) - 0.25
        )

        const formatedPrincipalInterest = formatAmount(parseFloat(principalInterest as string ?? '0'))

        const formatedPrincipalInterestWithRateShift = formatAmount(
          parseFloat(principalInterestWithRateShift as string ?? '0'))

        const formatedPropertyTaxes = formatAmount(parseFloat(propertyTaxes ?? '0'))

        const uip = calculateUip(loanAmount)

        const total = calculateTotal(
          form,
          loans?.length,
          {
            principalInterest: parseFloat(formatedPrincipalInterest),
            propertyTaxes: parseFloat(formatedPropertyTaxes),
            homeInsurance,
            hoaDues: hoaDues ?? 0,
            pmi: 0,
          }
        )

        const totalWithRateShift = calculateTotal(
          form,
          loans?.length,
          {
            principalInterest: parseFloat(formatedPrincipalInterestWithRateShift),
            propertyTaxes: parseFloat(formatedPropertyTaxes),
            homeInsurance,
            hoaDues: hoaDues ?? 0,
            pmi: 0,
          }
        )

      const interestChange = formatAmount(Math.abs(total - totalWithRateShift));

        const deposit_money_price = calculateDepositMoneyPrice(
          form,
          loans?.length,
          {
            propertyValue,
            purchasePrice: purchasePriceValue
          }
        );

        const loanToValue = calculateLoanToValue(
          form,
          loans?.length,
          {
            loanAmount,
            propertyValue,
            purchasePrice: purchasePriceValue
          }
        )

        const newColumns = [
          ...(loans || []),
          {
            ...defaultColumn,
            address: isEmptyAddress ? '' : address,
            zipCode,
            zpid,
            suplimentalData,
            purchasePrice: {
              value: purchasePriceValue,
            },
            propertyTaxes: {
              value: formatedPropertyTaxes,
            },
            realEstateTaxes: {
              value: formatedPropertyTaxes,
            },
            hoaDues: {
              value: hoaDues,
            },
            supplyTaxBill: {
              value: supplyMentalTaxBill || 0,
            },
            rentalValue: {
              value: rentValue,
            },
            priceStatus: {
              value: priceStatus,
            },
            propertyTaxesPercentage: {
              value: taxRate
            },
            downPayment: {
              value: commafy(downPayment)
            },
            downPaymentPercentage: {
              value: '20'
            },
            interestRate: {
              value: interestRate
            },
            homeInsurance: {
              value: homeInsurance
            },
            loanAmount: {
              value: commafy(loanAmount)
            },
            loanAmountWithUip: {
              value: loanAmount
            },
            principalInterest: {
              value: principalInterest
            },
            state: {
              value: state
            },
            uip: {
              value: uip,
            },
            total: {
              value: total
            },
            interestChange: {
              value: interestChange
            },
            montlySavingIncrease: 0,
            deposit_money_price,
            externalLoan: {
              ...defaultColumn.externalLoan,
              loanToValue,
              propertyValue,
              vaFundingFee
            }
          },
        ];

        form.setFieldValue('loans', newColumns);

        setTypeNewCol(null);
        handleLoansUpdate?.({}, true);
        getReportClosingCosts?.();
        getReportLoanClosingCosts?.();
      });
    }
    else {
      const newColumns = [
        ...(loans || []),
        { ...defaultColumn, address: isEmptyAddress ? undefined : address, zipCode },
      ];

      form.setFieldValue('loans', newColumns);

      setTypeNewCol(null);
      handleLoansUpdate?.({}, true);
      // getReportClosingCosts?.();
      // getReportLoanClosingCosts?.();
    }
  };

  const debouncedSearch = useCallback(
    debounce(([location]) => {
      reportLocationSearch
        .fetch({ location })
        .then((res) => setProperties(res?.data.results))
        .then(() => setIsLoading(false));
    }, 1000),
    []
  );

  const handleSearch = (value: string) => {
    setIsLoading(true);

    setSearchValue(value ?? '');

    if (!value) return;

    setProperties(null);

    debouncedSearch(value);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    const target = event.target as HTMLInputElement;

    if (event.key !== 'Enter' || !target.value) return;

    if (isLoading) {
      event.preventDefault();

      return;
    }

    handleAddNewColumn({ address: target.value });
    // getReportLoanClosingCosts?.();
  };

  return (
    <div style={{ flex: '1 1 auto' }} className={styles.tableCol}>
      <Loading visible={reportPropertyDetails.loading || reportLocationSearch.loading} absolute />
      <div className={styles.tableHeadCell}>
        {typeNewCol === 'location' && (
          <Form.Item className="table-form-item">
            <Select
              showSearch
              onChange={(value) => handleAddNewColumn(JSON.parse(value))}
              onSearch={handleSearch}
              onInputKeyDown={handleKeyDown}
              autoClearSearchValue={false}
              popupClassName="select-popup"
              placement="bottomRight"
              loading={reportLocationSearch.loading}
              searchValue={searchValue}
              filterOption={false}
              dropdownStyle={!properties ? { display: 'none' } : {}}
              placeholder="Enter property address"
              optionLabelProp="address"
              open={Boolean(searchValue)}
              value={searchValue || undefined}
              options={
                Array.isArray(properties)
                  ? properties?.map((item) => ({
                    label: (
                      <div className={styles.optionRow}>
                        <Icon component={Location} />
                        <Typography.Title level={4} className={styles.city}>
                          {item.streetAddress}, {item.city}, {item.state}, {item.zipcode}
                        </Typography.Title>
                      </div>
                    ),
                    value: JSON.stringify({
                      address: `${item.streetAddress};${item.city}, ${item.state}, ${item.zipcode}`,
                      zipCode: item.zipcode,
                      zpid: item.zpid,
                    }),
                    address: `${item?.streetAddress}, ${item?.city}`,
                  }))
                  : [
                    {
                      label: (
                        <div className={styles.optionRow}>
                          <Icon component={Location} />
                          <Typography.Title level={4} className={styles.city}>
                            {properties?.streetAddress}, {properties?.city}, {properties?.state},{' '}
                            {properties?.zipcode}
                          </Typography.Title>
                        </div>
                      ),
                      value: JSON.stringify({
                        address: `${properties?.streetAddress};${properties?.city}, ${properties?.state},
                          ${properties?.zipcode}`,
                        zipCode: properties?.zipcode,
                        zpid: properties?.zpid,
                      }),
                      address: `${properties?.streetAddress}, ${properties?.city}`,
                    },
                  ]
              }
            />
            <Button type="ghost" size="small" icon={<Icon component={Plus} />} onClick={() => handleAddNewColumn({})}>
              add blank column
            </Button>
            <Button type="ghost" size="small" icon={<Icon component={Close} />} onClick={handleClose}>
              {' '}
              close
            </Button>
          </Form.Item>
        )}
      </div>
    </div>
  );
};

export default ControlsCol;
